import { useEffect, useRef } from "react";

/**
 * Hook to execute a callback after a specified period of user inactivity.
 * @param timeout Timeout duration in milliseconds.
 * @param onIdle Callback function to execute when the idle timeout is reached.
 */
export const useIdleTimer = (timeout: number, onIdle: () => void) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const onIdleRef = useRef(onIdle);
  const lastActivityTimeRef = useRef<number>(Date.now());
  const blinkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const checkIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const focusIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const originalTitleRef = useRef<string>(document.title);
  const originalFaviconRef = useRef<string | null>(null);
  const shouldShowAlertRef = useRef<boolean>(false);
  const notificationRef = useRef<Notification | null>(null);

  // Update the ref to the latest callback to avoid re-triggering the effect
  useEffect(() => {
    onIdleRef.current = onIdle;
  }, [onIdle]);

  // Request notification permission on mount
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    const events = [
      "mousemove",
      "keydown",
      "wheel",
      "DOMMouseScroll",
      "mousewheel",
      "mousedown",
      "touchstart",
      "touchmove",
      "MSPointerDown",
      "MSPointerMove",
      "focus", // Also reset on window focus
    ];

    // Create colored favicon with highlight effect
    const createColoredFavicon = (color: string, highlight: boolean = true) => {
      const canvas = document.createElement('canvas');
      canvas.width = 32;
      canvas.height = 32;
      const ctx = canvas.getContext('2d');
      if (!ctx) return null;
      
      if (highlight) {
        // Add outer glow/highlight effect
        ctx.shadowColor = color;
        ctx.shadowBlur = 8;
        ctx.shadowOffsetX = 0;
        ctx.shadowOffsetY = 0;
      }
      
      // Draw outer white ring for emphasis
      ctx.beginPath();
      ctx.arc(16, 16, 15, 0, 2 * Math.PI);
      ctx.fillStyle = 'white';
      ctx.fill();
      
      // Draw main colored circle
      ctx.shadowBlur = 0;
      ctx.beginPath();
      ctx.arc(16, 16, 13, 0, 2 * Math.PI);
      ctx.fillStyle = color;
      ctx.fill();
      
      // Add inner dark border for contrast
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.lineWidth = 1;
      ctx.stroke();
      
      // Add white exclamation mark
      ctx.shadowBlur = 0;
      ctx.fillStyle = 'white';
      ctx.font = 'bold 22px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('!', 16, 16);
      
      // Add shadow to exclamation mark for depth
      ctx.fillStyle = 'rgba(0, 0, 0, 0.3)';
      ctx.font = 'bold 22px Arial';
      ctx.fillText('!', 16.5, 16.5);
      
      return canvas.toDataURL();
    };

    const setFavicon = (href: string) => {
      let link = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = href;
    };

    const startTitleBlink = () => {
      if (blinkIntervalRef.current) return; // Already blinking
      
      // Save original favicon
      const existingFavicon = document.querySelector("link[rel*='icon']") as HTMLLinkElement;
      originalFaviconRef.current = existingFavicon?.href || null;
      
      originalTitleRef.current = document.title;
      let blinkState = 0;
      const blinkData = [
        { title: "⚠️⚠️⚠️ ATTENTION NEEDED ⚠️⚠️⚠️", color: "#FF0000" },
        { title: "🔴🔴🔴 PLEASE COME BACK 🔴🔴🔴", color: "#FFA500" },
        { title: "❗❗❗ ACTION REQUIRED ❗❗❗", color: "#FF4444" },
        { title: "🚨🚨🚨 IDLE TIMEOUT 🚨🚨🚨", color: "#FF1744" }
      ];
      
      blinkIntervalRef.current = setInterval(() => {
        const current = blinkData[blinkState % blinkData.length];
        document.title = current.title;
        const faviconUrl = createColoredFavicon(current.color, true);
        if (faviconUrl) {
          setFavicon(faviconUrl);
        }
        blinkState++;
      }, 500);
    };

    const stopTitleBlink = () => {
      if (blinkIntervalRef.current) {
        clearInterval(blinkIntervalRef.current);
        blinkIntervalRef.current = null;
        document.title = originalTitleRef.current;
        
        // Restore original favicon
        if (originalFaviconRef.current) {
          setFavicon(originalFaviconRef.current);
        }
      }
      
      // Stop focus attempts
      if (focusIntervalRef.current) {
        clearInterval(focusIntervalRef.current);
        focusIntervalRef.current = null;
      }
      
      // Close any open notification
      if (notificationRef.current) {
        notificationRef.current.close();
        notificationRef.current = null;
      }
    };

    const startTaskbarFlash = () => {
      // Repeatedly attempt to focus the window to make taskbar flash
      // This won't actually focus the window from background, but will flash the taskbar
      let flashCount = 0;
      focusIntervalRef.current = setInterval(() => {
        try {
          window.focus();
          flashCount++;
          // Stop after 30 attempts (15 seconds)
          if (flashCount > 30) {
            if (focusIntervalRef.current) {
              clearInterval(focusIntervalRef.current);
              focusIntervalRef.current = null;
            }
          }
        } catch (e) {
          // Ignore errors
        }
      }, 500);
    };

    const showNotification = () => {
      // Show browser notification to make taskbar flash
      if ('Notification' in window && Notification.permission === 'granted') {
        notificationRef.current = new Notification('⚠️ Idle Timeout Alert', {
          body: 'You have been idle. Please return to the application.',
          icon: createColoredFavicon('#FF0000', true) || undefined,
          requireInteraction: true, // Keeps notification visible until user interacts
          tag: 'idle-alert', // Replaces previous notifications with same tag
          badge: createColoredFavicon('#FF0000', true) || undefined,
          silent: false,
        });
        
        // Focus window when notification is clicked
        notificationRef.current.onclick = () => {
          window.focus();
          if (notificationRef.current) {
            notificationRef.current.close();
            notificationRef.current = null;
          }
        };
      } else if ('Notification' in window && Notification.permission === 'default') {
        // Request permission if not yet decided
        Notification.requestPermission().then(permission => {
          if (permission === 'granted') {
            showNotification();
          }
        });
      }
    };

    const resetTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      lastActivityTimeRef.current = Date.now();
      shouldShowAlertRef.current = false;
      timerRef.current = setTimeout(() => {
        // If tab is visible, show alert immediately (browser will flash taskbar naturally)
        if (!document.hidden) {
          onIdleRef.current();
        }
        // If tab is hidden, start blinking, notification, and taskbar flash
        else {
          startTitleBlink();
          startTaskbarFlash(); // Make taskbar flash
          showNotification(); // Additional notification for emphasis
          shouldShowAlertRef.current = true; // Mark to show alert when user returns
        }
      }, timeout);
    };

    const handleActivity = () => {
      stopTitleBlink(); // Stop blinking on any activity
      resetTimer();
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab became hidden - start checking periodically if timeout exceeded
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        
        // Check every second if timeout has been exceeded
        checkIntervalRef.current = setInterval(() => {
          const idleTime = Date.now() - lastActivityTimeRef.current;
          if (idleTime >= timeout && !shouldShowAlertRef.current) {
            // Start blinking when timeout is exceeded in background
            startTitleBlink();
            startTaskbarFlash(); // Make taskbar flash
            showNotification(); // Show notification for additional emphasis
            shouldShowAlertRef.current = true; // Mark to show alert when user returns
          }
        }, 1000);
      } else {
        // Tab became visible - stop checking
        if (checkIntervalRef.current) {
          clearInterval(checkIntervalRef.current);
          checkIntervalRef.current = null;
        }
        
        stopTitleBlink(); // Stop blinking when tab becomes visible
        
        // Show alert if it was triggered while tab was hidden
        if (shouldShowAlertRef.current) {
          shouldShowAlertRef.current = false;
          onIdleRef.current(); // This will cause taskbar to flash naturally
        }
        
        // Always reset timer when tab becomes visible
        resetTimer();
      }
    };

    // Initialize timer
    resetTimer();

    // Attach event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity);
    });

    // Add visibility change listener
    document.addEventListener("visibilitychange", handleVisibilityChange);

    // Cleanup
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
      stopTitleBlink();
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [timeout]);
};
